const QueryParams = require('../../../common/frontend/helpers/params');
const urlUpdate = require('../../../common/frontend/helpers/url_update');
const DealFetcher = require('./helpers/deal_fetcher');
const PaginationRenderer = require('./helpers/pagination_renderer');
const Spinner = require('./helpers/spinner');
const scrollPositionHelper = require('../../../common/frontend/helpers/scroll_position');
const carouselScrollPositionsHelper = require('../../../common/frontend/helpers/carousel_scroll_positions');
const adHelper = require('../../../ads/helpers/ad-helper');
const { windowLoaded } = require('@grpn/document-states');
const { itemExists } = require('./helpers/local_storage');
const taxonomyPreferences = require('./helpers/taxonomy_preferences');
const {
  bucketAAExperiment,
} = require('../../../common/frontend/helpers/finch');

const SELECTORS = {
  ALL_CARDS: '.card-ui, .location-rememberance',
  CARD_SHELLS: '#card-shells',
  LAZY_LOADED: 'div[data-is-lazy-loaded="true"]',
  SMALL_FEATURED: '#small-featured',
};

const context = {
  initialize() {
    context.loadApp();
  },
  loadApp() {
    context.setDefaults();
    context.initializeObjects();
    context.attachListeners();
    context.initializeAAExperiment();
    windowLoaded.then(() => {
      context.onLoad();
    });
  },
  setDefaults() {
    context.urlParams = Groupon.Utils.unparam();
  },
  onLoad() {
    context.lazyLoadAssets();
    context.restorePreviousState();
  },
  initializeObjects() {
    context.domConfig = Groupon.Utils.getConfigFromDom();
    context.spinner = new Spinner();
    context.dealFetcher = new DealFetcher(context.domConfig.csrfToken);
    context.paginationRenderer = new PaginationRenderer();
    context.intersectionObserver = new IntersectionObserver(
      this.onScrollFetchLazyLoadedCardsHandler,
      {
        root: null,
        rootMargin: '0px 0px 600px 0px',
        threshold: 0.01,
      }
    );
  },
  attachListeners() {
    window.addEventListener('popstate', context.handleOnPopState);
  },
  bucketAAExperiment(entries) {
    if (entries.some(entry => entry.isIntersecting)) {
      bucketAAExperiment('20-01_AA1_WebHomepage');
      context.aaIntersectionObserver.disconnect();
    }
  },
  initializeAAExperiment() {
    context.aaIntersectionObserver = new IntersectionObserver(
      context.bucketAAExperiment
    );
    const threeUpCollection = document.querySelector(SELECTORS.SMALL_FEATURED);
    if (threeUpCollection) {
      context.aaIntersectionObserver.observe(threeUpCollection);
    }
  },
  handleOnPopState() {
    const newUrlParams = Groupon.Utils.unparam();
    if (context.urlParams.page === newUrlParams.page) {
      return;
    }

    const page = context.getPageNumberFromUrl(window.location.href);

    context.fetchPaginatedCards(page);
  },
  lazyLoadAssets() {
    import(
      /* webpackChunkName: "browse-card-ui" */ 'grpn-card-ui/frontend/init'
    ).then(context.onLazyLoadedCardUiJavascript);

    if (
      context.domConfig.showTaxonomyPreferences &&
      !itemExists('shownTaxonomyPreferences')
    ) {
      import(
        /* webpackChunkName: "home-desktop-taxonomy-css" */ './styles/home_desktop_taxonomy.styl'
      );
      import(
        /* webpackChunkName: "home-desktop-taxonomy" */ './home_desktop_taxonomy'
      ).then(context.onLazyLoadedTaxonomy);
    }
  },

  onLazyLoadedCardUiJavascript() {
    window.CardUI.init();
  },
  onLazyLoadedTaxonomy() {
    const showDialog = function (selectedPreferences) {
      window.taxonomyDialogLoader.showTaxonomyDialog.call(
        context,
        GIG.Modal,
        window.taxonomyDialogLoader.onUserClickedTaxonomyTile,
        selectedPreferences
      );
    };

    try {
      taxonomyPreferences
        .fetch({ _csrf: context.domConfig.csrfToken })
        .then(selectedPreferences => showDialog(selectedPreferences));
    } catch (e) {
      showDialog();
    }
  },
  lazyLoadCss(href) {
    const link = document.createElement('link');
    link.css = 'text/javascript';
    link.rel = 'stylesheet';
    link.href = href;
    document.getElementsByTagName('head')[0].appendChild(link);
  },
  onPaginationClick(event) {
    event.preventDefault();

    const page = context.getPageNumberFromUrl(event.currentTarget.href);

    const paramObject = Groupon.Utils.unparam();
    paramObject.page = page;

    const updatedUrl = `${window.location.pathname}?${QueryParams.toString(
      paramObject
    )}`;
    urlUpdate.update(updatedUrl, { pushState: true });
    context.fetchPaginatedCards(page);
  },
  getPageNumberFromUrl(url) {
    let page = 1;

    const queryParams = url.split('?')[1];

    if (queryParams && queryParams.length) {
      const queryParamHash = Groupon.Utils.unparam(queryParams);

      if (queryParamHash.page) {
        page = queryParamHash.page;
      }
    }

    return page;
  },
  getLazyLoadedSectionIndex() {
    context.lazyLoadedSectionIndex = !context.lazyLoadedSectionIndex
      ? 1
      : ++context.lazyLoadedSectionIndex;

    return context.lazyLoadedSectionIndex;
  },
  attachOnScrollFetchLazyLoadedCardsListener() {
    const cardShells = document.querySelector(SELECTORS.CARD_SHELLS);
    if (cardShells) {
      context.intersectionObserver.observe(cardShells);
    }
  },
  onScrollFetchLazyLoadedCardsHandler() {
    context.fetchLazyLoadedCards();
    context.intersectionObserver.disconnect();
  },
  fetchLazyLoadedCards() {
    const cardShells = document.querySelector(SELECTORS.CARD_SHELLS);
    if (!cardShells) {
      return;
    }

    context.dealFetcher
      .fetchLazyLoadedCards(context.getLazyLoadedSectionIndex())
      .then(context.handleLazyLoadedCardsFetchedSuccess)
      .catch(context.handleLazyLoadedCardsFetchedError);
  },
  fetchPaginatedCards(page) {
    const cardShells = document.querySelector(SELECTORS.CARD_SHELLS);
    if (cardShells) {
      return;
    }

    context.spinner.start();

    context.dealFetcher
      .fetchPaginatedCards(page)
      .then(context.handlePaginatedCardsFetchedSuccess)
      .catch(context.handlePaginatedCardsFetchedError)
      .then(context.spinner.stop.bind(context.spinner));
  },
  handleLazyLoadedCardsFetchedSuccess(response) {
    if (!response || !response.cardsHtml || !response.cardsHtml.length) {
      context.paginationRenderer.clearCardShells();
      return;
    }

    const scrollPos =
      window.scrollY ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;
    context.paginationRenderer.clearCardShells();
    context.paginationRenderer.insertCards(response.cardsHtml);
    window.scrollTo(0, scrollPos);

    context.paginationRenderer.attachPaginationClickHandler(
      context.onPaginationClick
    );

    context.attachCardClickHandlers();
    context.attachOnScrollFetchLazyLoadedCardsListener();
    context.attachModalHandlers();
  },
  /* eslint no-empty: "error" */
  handleLazyLoadedCardsFetchedError() {},
  handlePaginatedCardsFetchedSuccess(response) {
    if (!response || !response.cardsHtml || !response.cardsHtml.length) {
      return;
    }

    context.paginationRenderer.clearPaginatedCards();
    context.paginationRenderer.insertCards(response.cardsHtml);
    context.paginationRenderer.scrollToTop();
    context.paginationRenderer.attachPaginationClickHandler(
      context.onPaginationClick
    );

    context.attachCardClickHandlers();
    context.urlParams = Groupon.Utils.unparam();
  },
  /* eslint no-empty: "error" */
  handlePaginatedCardsFetchedError() {},
  attachCardClickHandlers() {
    const allCards = document.querySelectorAll(SELECTORS.ALL_CARDS);

    if (allCards && allCards.length) {
      allCards.forEach(card => {
        card.addEventListener('click', context.onCardClicked);
      });
    }
  },

  attachModalHandlers() {
    if (GIG && GIG.Modal) {
      GIG.Modal.init();
    }
  },

  onCardClicked() {
    const cardsHtml = document.querySelectorAll(SELECTORS.LAZY_LOADED);

    context.setCardsAndScrollPositionInBrowserHistory({
      lazyLoadedCardsHtml: context.sanitizeCardsHtml(cardsHtml),
      carouselScrollPositions:
        carouselScrollPositionsHelper.getCarouselScrollPositions(),
      scrollPosition: scrollPositionHelper.getScrollPosition(),
    });
  },
  setCardsAndScrollPositionInBrowserHistory({
    lazyLoadedCardsHtml,
    carouselScrollPositions,
    scrollPosition,
  } = {}) {
    const page = context.getPageNumberFromUrl(window.location.href);

    const state = {
      lazyLoadedCardsHtml,
      lazyLoadedSectionIndex: context.lazyLoadedSectionIndex,
      carouselScrollPositions,
      scrollPosition,
      page,
    };

    history.replaceState(state, `Page ${page}`, window.location.href);
  },
  sanitizeCardsHtml(cardsHtml) {
    const combinedHtml = [];
    if (cardsHtml && cardsHtml.length) {
      cardsHtml.forEach(node => {
        if (
          node.children &&
          node.children[0].classList.contains(
            'ai-inventory-placement-container'
          )
        ) {
          node = node.cloneNode(true);
          adHelper.sanitizeAd(node.children[0]);
        }
        const htmlString = node.outerHTML.replace(
          /has-scroll-listener|has-viewportenter-listener|listener-added/g,
          ''
        );
        combinedHtml.push(htmlString);
      });
    }
    return combinedHtml.join('');
  },
  restorePreviousState() {
    const state = history.state || {};
    const {
      lazyLoadedCardsHtml,
      lazyLoadedSectionIndex,
      carouselScrollPositions,
      scrollPosition,
      page,
    } = state;

    if (
      lazyLoadedCardsHtml &&
      lazyLoadedSectionIndex &&
      carouselScrollPositions &&
      scrollPosition != null &&
      page === context.getPageNumberFromUrl(window.location.href)
    ) {
      context.lazyLoadedSectionIndex = lazyLoadedSectionIndex;

      context.handleLazyLoadedCardsFetchedSuccess({
        cardsHtml: lazyLoadedCardsHtml,
      });

      carouselScrollPositionsHelper.restoreCarouselScrollPositions(
        carouselScrollPositions
      );

      window.scrollTo(0, scrollPosition);
    } else {
      context.fetchLazyLoadedCards();
    }

    context.setCardsAndScrollPositionInBrowserHistory();
  },
};

export default context;
